import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import TimeLogModal from '@/components/TimeLogModal';
import QCSheetSelectorModal from '@/components/QCSheetSelectorModal';
import ChecklistSelectorModal from '@/components/ChecklistSelectorModal';
import { 
  ArrowLeft, MessageSquare, Clock, AlertCircle, 
  CheckCircle, FileText, Send, Edit, Trash,
  ClipboardCheck, Package, Settings, Eye, FolderOpen, Download, Trash2, Image, File
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WorkBoardProjectDetail = ({ user }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [memos, setMemos] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const [showTimeLogModal, setShowTimeLogModal] = useState(false);
  const [showQCModal, setShowQCModal] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [showJobFolderModal, setShowJobFolderModal] = useState(false);
  
  // New Memo Form
  const [newMemo, setNewMemo] = useState({
    memo_type: 'note',
    priority: 'normal',
    subject: '',
    message: ''
  });

  useEffect(() => {
    loadProjectData();
  }, [projectId]);

  const loadProjectData = async () => {
    setLoading(true);
    try {
      const [projectRes, memosRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/memos`)
      ]);
      
      setProject(projectRes.data);
      setMemos(memosRes.data.memos || []);
      
      // Load time logs for this project
      try {
        const timeRes = await axios.get(`${API}/time-tracker/project/${projectId}`);
        setTimeLogs(timeRes.data.time_logs || []);
      } catch (err) {
        console.log('Time logs not available');
      }
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/workboard');
    } finally {
      setLoading(false);
    }
  };

  const createMemo = async () => {
    if (!newMemo.subject || !newMemo.message) {
      toast.error('Subject and message are required');
      return;
    }
    
    try {
      await axios.post(`${API}/projects/${projectId}/memos`, newMemo);
      toast.success('Memo added');
      setNewMemo({
        memo_type: 'note',
        priority: 'normal',
        subject: '',
        message: ''
      });
      loadProjectData();
    } catch (error) {
      toast.error('Failed to create memo');
    }
  };

  const getMemoTypeColor = (type) => {
    const colors = {
      note: 'bg-blue-500',
      issue: 'bg-red-500',
      progress: 'bg-green-500',
      request: 'bg-yellow-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-500',
      normal: 'bg-blue-500',
      high: 'bg-orange-500',
      urgent: 'bg-red-500'
    };
    return colors[priority] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Clock className="animate-spin" size={48} />
      </div>
    );
  }

  if (!project) {
    return null;
  }

  const totalHours = timeLogs.reduce((sum, log) => sum + (log.hours || 0), 0);

  return (
    <div className="min-h-screen bg-background p-6 space-y-6">
      {/* Header */}
      <div>
        <Button 
          variant="ghost" 
          onClick={() => navigate('/workboard')}
          className="mb-4"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to WorkBoard
        </Button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-black uppercase tracking-tight">{project.project_name}</h1>
            <p className="text-muted-foreground mt-2">
              Customer: {project.client_name} • {project.client_email}
            </p>
          </div>
          <Badge className="text-lg px-4 py-2">{project.status}</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Project Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project Details */}
          <Card>
            <CardHeader>
              <CardTitle>Project Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-mono uppercase text-muted-foreground">Quote ID</label>
                  <p className="font-medium">{project.quote_id || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-mono uppercase text-muted-foreground">Job Folder</label>
                  <p className="font-medium">
                    {project.job_folder ? 
                      `${Object.keys(project.job_folder).length} Categories` : 
                      'N/A'
                    }
                  </p>
                </div>
                <div>
                  <label className="text-sm font-mono uppercase text-muted-foreground">Start Date</label>
                  <p className="font-medium">
                    {project.start_date ? new Date(project.start_date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-mono uppercase text-muted-foreground">Completion Date</label>
                  <p className="font-medium">
                    {project.completion_date ? new Date(project.completion_date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-sm font-mono uppercase text-muted-foreground">Description</label>
                <p className="text-sm mt-1">{project.description || 'No description'}</p>
              </div>

              <div>
                <label className="text-sm font-mono uppercase text-muted-foreground">Labour Hours</label>
                <div className="flex items-center gap-4 mt-2">
                  <div className="text-3xl font-black">{totalHours.toFixed(1)} hrs</div>
                  <div className="text-sm text-muted-foreground">
                    Estimated: {project.estimated_labour_hours || 0} hrs
                  </div>
                </div>
                <div className="w-full bg-border rounded-full h-3 mt-2">
                  <div 
                    className="bg-primary rounded-full h-3 transition-all"
                    style={{ 
                      width: `${Math.min((totalHours / (project.estimated_labour_hours || 1)) * 100, 100)}%` 
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-3">
                <Button 
                  variant="outline"
                  className="flex-col h-20 gap-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
                  onClick={() => setShowTimeLogModal(true)}
                >
                  <Clock size={20} />
                  <span className="text-xs">Log Time</span>
                </Button>
                <Button 
                  variant="outline"
                  className="flex-col h-20 gap-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
                  onClick={() => setShowQCModal(true)}
                >
                  <FileText size={20} />
                  <span className="text-xs">QC Sheets</span>
                </Button>
                <Button 
                  variant="outline"
                  className="flex-col h-20 gap-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
                  onClick={() => setShowChecklistModal(true)}
                >
                  <ClipboardCheck size={20} />
                  <span className="text-xs">Checklists</span>
                </Button>
                <Button 
                  variant="outline"
                  className="flex-col h-20 gap-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
                  onClick={() => navigate(`/tools/rfq?project_id=${projectId}`)}
                >
                  <Send size={20} />
                  <span className="text-xs">Send RFQ</span>
                </Button>
                <Button 
                  variant="outline"
                  className="flex-col h-20 gap-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
                  onClick={() => navigate(`/tools/inventory?project_id=${projectId}`)}
                >
                  <Package size={20} />
                  <span className="text-xs">Inventory</span>
                </Button>
                <Button 
                  variant="outline"
                  className="flex-col h-20 gap-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
                  onClick={() => navigate(`/tools/decision-tree?project_id=${projectId}`)}
                >
                  <Settings size={20} />
                  <span className="text-xs">Decisions</span>
                </Button>
                <Button 
                  variant="outline"
                  className="flex-col h-20 gap-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
                  onClick={() => navigate('/workboard')}
                >
                  <ArrowLeft size={20} />
                  <span className="text-xs">Back to Board</span>
                </Button>
                <Button 
                  variant="outline"
                  className="flex-col h-20 gap-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
                  onClick={() => navigate(`/projects/${projectId}`)}
                >
                  <FolderOpen size={20} />
                  <span className="text-xs">View Files</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* QC Sheet Selector Modal */}
          <QCSheetSelectorModal
            open={showQCModal}
            onClose={() => setShowQCModal(false)}
            projectId={projectId}
            projectName={project?.project_name}
          />

          {/* Checklist Selector Modal */}
          <ChecklistSelectorModal
            open={showChecklistModal}
            onClose={() => setShowChecklistModal(false)}
            projectId={projectId}
            projectName={project?.project_name}
          />

          {/* Project Milestones & Sub-Tasks */}
          {project.milestones && project.milestones.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Project Milestones & Sub-Tasks</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {project.milestones.map((milestone) => {
                    const subTasks = milestone.sub_tasks || [];
                    return (
                      <div 
                        key={milestone.id} 
                        className="p-3 border border-border rounded-sm bg-accent/20"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle 
                              size={18} 
                              className={milestone.completed ? "text-green-500" : "text-muted-foreground"}
                            />
                            <span className={`text-sm font-semibold text-gray-900 ${milestone.completed ? 'line-through text-muted-foreground' : ''}`}>
                              {milestone.description}
                            </span>
                            {subTasks.length > 0 && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-bold">
                                {subTasks.filter(st => st.status === 'completed').length}/{subTasks.length} tasks
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-mono text-gray-700 font-bold">
                            Progress: {(milestone.hours_spent || 0).toFixed(1)}h / {milestone.hours_estimated}h
                        </span>
                        <span className="text-xs font-mono font-bold">
                          {Math.min(Math.round(((milestone.hours_spent || 0) / milestone.hours_estimated) * 100), 100)}%
                        </span>
                      </div>
                      
                      {/* Overcapacity Warning */}
                      {(() => {
                        const totalSubTaskHours = subTasks.reduce((sum, st) => sum + (st.estimated_hours || 0), 0);
                        const exceeds = totalSubTaskHours > milestone.hours_estimated;
                        return exceeds && (
                          <div className="flex items-center gap-2 mb-2 p-2 bg-red-50 border-l-4 border-red-500 rounded">
                            <AlertCircle size={14} className="text-red-600 flex-shrink-0" />
                            <span className="text-xs text-red-700 font-bold">
                              ⚠️ Sub-tasks exceed milestone by {(totalSubTaskHours - milestone.hours_estimated).toFixed(1)}h - Supervisor review required
                            </span>
                          </div>
                        );
                      })()}
                      
                      <div className="w-full bg-border rounded-full h-2 mb-3">
                        <div 
                          className={`rounded-full h-2 ${milestone.completed ? 'bg-green-500' : 'bg-primary'}`}
                          style={{ 
                            width: `${Math.min(((milestone.hours_spent || 0) / milestone.hours_estimated) * 100, 100)}%` 
                          }}
                        />
                      </div>

                      {/* Sub-Tasks */}
                      {subTasks.length > 0 && (
                        <div className="mt-3 ml-2 space-y-2 border-t border-gray-200 pt-2">
                          <p className="text-xs font-bold text-gray-700 uppercase">
                            Sub-Tasks
                          </p>
                          {subTasks.map((subTask) => (
                            <div key={subTask.id} className="flex items-start gap-2 text-sm bg-white p-2 rounded border border-gray-200">
                              <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                                subTask.status === 'completed' ? 'bg-green-500' :
                                subTask.status === 'in_progress' ? 'bg-blue-500' :
                                'bg-gray-400'
                              }`} />
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900">{subTask.name}</p>
                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-700">
                                  <span className="font-mono font-bold">{subTask.actual_hours || 0}h / {subTask.estimated_hours}h</span>
                                  {subTask.assigned_to && (
                                    <span className="font-medium">👤 {subTask.assigned_to}</span>
                                  )}
                                  <span className={`font-bold ${
                                    subTask.status === 'completed' ? 'text-green-700' :
                                    subTask.status === 'in_progress' ? 'text-blue-700' :
                                    'text-gray-700'
                                  }`}>
                                    {subTask.status.replace('_', ' ').toUpperCase()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Team Message Board */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare size={20} />
                Team Message Board
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* New Memo Form */}
              <Card className="bg-accent/20 border-primary/30">
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-mono uppercase text-muted-foreground">Type</label>
                      <select 
                        className="w-full mt-1 px-3 py-2 border rounded-sm bg-background text-sm"
                        value={newMemo.memo_type}
                        onChange={(e) => setNewMemo({...newMemo, memo_type: e.target.value})}
                      >
                        <option value="note">Note</option>
                        <option value="issue">Issue</option>
                        <option value="progress">Progress Update</option>
                        <option value="request">Request</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-mono uppercase text-muted-foreground">Priority</label>
                      <select 
                        className="w-full mt-1 px-3 py-2 border rounded-sm bg-background text-sm"
                        value={newMemo.priority}
                        onChange={(e) => setNewMemo({...newMemo, priority: e.target.value})}
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs font-mono uppercase text-muted-foreground">Subject</label>
                    <Input 
                      value={newMemo.subject}
                      onChange={(e) => setNewMemo({...newMemo, subject: e.target.value})}
                      placeholder="Brief subject line"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs font-mono uppercase text-muted-foreground">Message</label>
                    <Textarea 
                      value={newMemo.message}
                      onChange={(e) => setNewMemo({...newMemo, message: e.target.value})}
                      placeholder="Detailed message..."
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                  
                  <Button onClick={createMemo} className="w-full">
                    <Send size={16} className="mr-2" />
                    Post Message
                  </Button>
                </CardContent>
              </Card>

              {/* Memo List */}
              <div className="space-y-3">
                {memos.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No messages yet</p>
                ) : (
                  memos.map(memo => (
                    <Card key={memo.id} className="border-l-4" style={{ borderLeftColor: getPriorityColor(memo.priority) }}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex gap-2">
                            <Badge className={getMemoTypeColor(memo.memo_type)}>
                              {memo.memo_type}
                            </Badge>
                            <Badge className={getPriorityColor(memo.priority)}>
                              {memo.priority}
                            </Badge>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {new Date(memo.created_at).toLocaleString()}
                          </span>
                        </div>
                        
                        <h4 className="font-bold mb-1">{memo.subject}</h4>
                        <p className="text-sm text-muted-foreground mb-2">{memo.message}</p>
                        
                        <div className="flex items-center justify-between pt-2 border-t">
                          <span className="text-xs font-medium">{memo.author_name}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Time Logs */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Time Logs</CardTitle>
            </CardHeader>
            <CardContent>
              {timeLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No time logged yet</p>
              ) : (
                <div className="space-y-3">
                  {timeLogs.map(log => (
                    <div key={log.id} className="p-3 border border-border rounded-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{log.employee_name}</span>
                        <span className="text-sm font-mono font-bold">{log.hours}h</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{log.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(log.date).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Time Log Modal */}
      <TimeLogModal
        isOpen={showTimeLogModal}
        onClose={() => setShowTimeLogModal(false)}
        project={project}
        onTimeLogged={loadProjectData}
      />
    </div>
  );
};

export default WorkBoardProjectDetail;
